import { Fragment, useState, useEffect, type ComponentType } from "react";
import Logo from "../../../public/ITDept.jpeg";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Ticket,
  ShieldCheck,
  LogOut,
  Server,
  Laptop,
  Code,
  History,
  Trash2,
  ChevronUp,
  ChevronDown,
  Layers,
  Users,
  Megaphone,
  Network,
  Key,
  type LucideProps,
} from "lucide-react";
import { cn } from "../../lib/cn";
import { useAuth } from "../../context/AuthContext";
import { Avatar } from "../ui/Avatar";
import { SpotlightCard } from "../ui/SpotlightCard";
import type { Role } from "../../lib/api";
import { UserDropdown } from "./UserDropdown";
import { EditProfileModal } from "./EditProfileModal";
import { trackBrandInteraction } from "../../lib/easterEggs";
import { toast } from "sonner";

interface NavItem {
  to?: string;
  label: string;
  icon: ComponentType<LucideProps>;
  roles: Role[];
  dividerBefore?: boolean;
  dividerAfter?: boolean;
  children?: { to: string; label: string; icon: ComponentType<LucideProps> }[];
}

const NAV: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["AGENT", "IT", "ADMIN"] },
  { to: "/tickets", label: "Tickets", icon: Ticket, roles: ["AGENT", "IT", "ADMIN"] },
  { to: "/department-tickets", label: "Department Tickets", icon: Users, roles: ["AGENT"] },
  { to: "/announcements", label: "Announcements", icon: Megaphone, roles: ["AGENT", "IT", "ADMIN"] },
  {
    label: "Issue Trackers",
    icon: Layers,
    roles: ["IT", "ADMIN"],
    dividerBefore: true,
    dividerAfter: true,
    children: [
      { to: "/system-tracker", label: "System Tracker", icon: Server },
      { to: "/hardware-tracker", label: "Hardware Tracker", icon: Laptop },
      { to: "/software-tracker", label: "Software Tracker", icon: Code },
      { to: "/network-tracker", label: "Network Tracker", icon: Network },
      { to: "/access-tracker", label: "Access Tracker", icon: Key },
    ],
  },
  { to: "/history", label: "History", icon: History, roles: ["AGENT", "IT", "ADMIN"] },
  { to: "/management", label: "Management", icon: ShieldCheck, roles: ["ADMIN"] },
];

function NavGroup({ item, location }: { item: NavItem; location: any }) {
  const isActive = item.children?.some((child) => location.pathname === child.to);
  const [isOpen, setIsOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) setIsOpen(true);
  }, [isActive]);

  return (
    <Fragment key={item.label}>
      {item.dividerBefore && <div className="h-px bg-white/5 my-2" />}
      <div className="flex flex-col gap-1">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "group flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all w-full",
            isActive && !isOpen
              ? "text-white bg-white/[0.04]"
              : "text-slate-300 hover:text-white hover:bg-white/[0.04]",
          )}
        >
          <div className="flex items-center gap-3">
            <item.icon className="w-4 h-4 shrink-0" />
            {item.label}
          </div>
          <ChevronDown
            className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")}
          />
        </button>

        {isOpen && item.children && (
          <div className="flex flex-col gap-1 pl-4 mt-1">
            {item.children.map((child) => (
              <NavLink
                key={child.to}
                to={child.to}
                className={({ isActive }) =>
                  cn(
                    "group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
                    isActive
                      ? "bg-gradient-to-r from-brand-500/30 to-brand-600/10 text-white border border-brand-400/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
                      : "text-slate-400 hover:text-white hover:bg-white/[0.04] border border-transparent",
                  )
                }
              >
                <child.icon className="w-4 h-4 shrink-0" />
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
      {item.dividerAfter && <div className="h-px bg-white/5 my-2" />}
    </Fragment>
  );
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [editingProfile, setEditingProfile] = useState(false);
  if (!user) return null;
  const items = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <aside className="flex flex-col w-64 shrink-0 sticky top-0 h-screen p-4">
      <SpotlightCard
        padded={false}
        spotlightColor="rgba(0, 86, 255, 0.25)"
        className="flex flex-col h-full p-4"
      >
        {/* Brand */}
        <div
          className="flex items-center gap-3 px-2 py-2 mb-6 cursor-default select-none"
          onClick={() => {
            const result = trackBrandInteraction();
            if (result.triggered) {
              toast("🎉 Built with ❤️ by JCFP & SAD", {
                description: "Jan Carlo F. Perez & Seth Andrei Dimaano",
                duration: 3500,
              });
            }
          }}
        >
          <img src={Logo} alt="Logo" className="w-10 h-10 rounded-xl shadow-glow object-cover" />
          <div>
            <div className="text-white font-semibold leading-tight">IMPACT Pro</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">
              Support Desk
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex flex-col gap-1 flex-1 overflow-y-auto pr-1">
          {items.map((item) => {
            if (item.children) {
              return <NavGroup key={item.label} item={item} location={location} />;
            }
            const { to, label, icon: Icon, dividerBefore, dividerAfter } = item;
            return (
              <Fragment key={to! + label}>
                {dividerBefore && <div className="h-px bg-white/5 my-2" />}
                <NavLink
                  to={to!}
                  end={to === "/dashboard"}
                  className={({ isActive }) => {
                    const url = new URL(to!, window.location.origin);
                    const searchParams = new URLSearchParams(url.search);
                    const activeParams = new URLSearchParams(location.search);

                    let active = isActive;
                    if (searchParams.has("category")) {
                      active =
                        isActive &&
                        activeParams.get("category") === searchParams.get("category");
                    } else if (
                      activeParams.has("category") &&
                      url.pathname === "/tickets"
                    ) {
                      active = false;
                    }

                    return cn(
                      "group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
                      active
                        ? "bg-gradient-to-r from-brand-500/30 to-brand-600/10 text-white border border-brand-400/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
                        : "text-slate-300 hover:text-white hover:bg-white/[0.04] border border-transparent",
                    );
                  }}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {label}
                </NavLink>
                {dividerAfter && <div className="h-px bg-white/5 my-2" />}
              </Fragment>
            );
          })}
        </nav>

        {/* Recycle Bin (IT only) */}
        {(user.role === "IT" || user.role === "ADMIN") && (
          <div className="mb-2">
            <NavLink
              to="/recycle-bin"
              className={({ isActive }) =>
                cn(
                  "group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
                  isActive
                    ? "bg-gradient-to-r from-brand-500/30 to-brand-600/10 text-white border border-brand-400/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
                    : "text-slate-300 hover:text-white hover:bg-white/[0.04] border border-transparent",
                )
              }
            >
              <Trash2 className="w-4 h-4 shrink-0" />
              Recycle Bin
            </NavLink>
          </div>
        )}

        {/* User card */}
        <UserDropdown align="top" onEditProfile={() => setEditingProfile(true)}>
          <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-3 w-full hover:bg-white/5 p-2 rounded-xl transition-colors">
            <Avatar name={user.name} url={user.avatarUrl} size="sm" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{user.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">
                {user.role}
              </div>
            </div>
            <ChevronUp className="w-4 h-4 text-slate-400" />
          </div>
        </UserDropdown>
      </SpotlightCard>

      <EditProfileModal open={editingProfile} onClose={() => setEditingProfile(false)} />
    </aside>
  );
}
